'use client';
import { useState, useRef, useEffect, MutableRefObject, SyntheticEvent } from 'react';

import { useAtomValue } from 'jotai';

import { currentVehicleAtom } from '@core/Atoms/Vehicle/CurrentVehicle.atom';
import { Loading } from '@core/Components/Loading/Loading';
import { PAGE_TYPE } from '@core/Constants/PageType';
import { Locale } from '@core/Entities/Locale/Locale.entity';

import { FavouritesButton } from '../FavouritesButton/FavouritesButton';

import { Arrow360 } from './Arrow360.icon';
import { useImage360Tracking } from './hooks/UseImage360Tracking';

import styles from './Image360.module.css';

interface Image360Props {
  images: string[];
  autoplayInterval?: number;
  galleryStrings: any;
}

export const Image360 = ({ images = [], autoplayInterval = 75, galleryStrings }: Image360Props) => {
  const currentIndexRef = useRef(Math.floor((images.length / 3) * 2));
  const [counter, setCounter] = useState(0);
  const [hasBeenUsed, setHasBeenUsed] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [imagesLoaded, setImagesLoaded] = useState(new Set());
  const [isInitialised, setIsInitialised] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const autoplayRef = useRef<NodeJS.Timeout | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const vehicle = useAtomValue(currentVehicleAtom)!;
  const imagesRef = useRef(null) as unknown as MutableRefObject<
    NodeListOf<HTMLElement> | undefined
  >;
  useImage360Tracking();

  const startAutoplay = () => {
    if (isPlaying) return;
    setIsPlaying(true);

    autoplayRef.current = setInterval(() => {
      currentIndexRef.current = (currentIndexRef.current + 1) % images.length;
      showImage(currentIndexRef.current);
    }, autoplayInterval);
  };

  const stopAutoplay = () => {
    setIsLoading(false);
    setIsPlaying(false);

    if (autoplayRef.current) clearInterval(autoplayRef.current);
  };

  const showImage = (newIndex: number) => {
    currentIndexRef.current = newIndex;
    imagesRef.current ??= containerRef.current?.querySelectorAll('img');

    if (!imagesRef.current) return;

    imagesRef.current.forEach($image => $image.setAttribute('data-is-active', 'false'));
    imagesRef.current[newIndex].setAttribute('data-is-active', 'true');
  };

  const onImageLoad = (e: SyntheticEvent<HTMLImageElement, Event>, index: number) => {
    e.currentTarget.setAttribute('data-complete', 'true');
    setImagesLoaded(set => {
      set.add(index);
      return new Set(set);
    });
  };

  useEffect(() => {
    showImage(currentIndexRef.current);

    for (let i = 0; i < images.length; i++) {
      const img = new Image();
      img.src = images[i];
      img.onload = () => {
        setImagesLoaded(set => {
          set.add(i);
          return new Set(set);
        });
        const imageEl = containerRef.current?.querySelector(`img[src="${img.src}"]`);
        imageEl?.setAttribute('data-complete', 'true');
      };
    }
  }, []);

  useEffect(() => {
    if (imagesLoaded.size < images.length) return;

    currentIndexRef.current = Math.floor((images.length / 3) * 2);
    setIsInitialised(true);
    startAutoplay();
    setTimeout(stopAutoplay, (images.length / 3) * autoplayInterval);
  }, [imagesLoaded]);

  useEffect(() => {
    const containerEl = containerRef.current;
    if (!containerEl) return;

    let isDragging = false;
    let startX = 0;
    let startY = 0;
    let startingIndex = 0;

    const handleStart = (clientX: number, e?: TouchEvent) => {
      isDragging = true;
      startX = clientX;
      startY = e ? e.touches[0].clientY : 0;
      startingIndex = currentIndexRef.current;
      setHasBeenUsed(true);
      stopAutoplay();
    };

    const handleMove = (clientX: number, e?: TouchEvent) => {
      if (!isDragging) return;
      const deltaX = clientX - startX;
      const deltaY = e ? e.touches[0].clientY - startY : 0;
      const threshold = 20;

      const imagesMoved = Math.ceil(deltaX / threshold);
      let newIndex = (startingIndex + imagesMoved) % images.length;
      if (newIndex < 0) newIndex = images.length + newIndex;
      showImage(newIndex);

      if (e) {
        if (Math.abs(deltaX) > Math.abs(deltaY)) e.preventDefault();
      }
    };

    const handleEnd = () => {
      isDragging = false;
      setCounter(prev => prev + 1);
    };

    const handleMouseDown = (e: MouseEvent) => handleStart(e.clientX);
    const handleMouseMove = (e: MouseEvent) => handleMove(e.clientX);
    const handleMouseUp = handleEnd;

    const handleTouchStart = (e: TouchEvent) => handleStart(e.touches[0].clientX, e);
    const handleTouchMove = (e: TouchEvent) => handleMove(e.touches[0].clientX, e);

    const handleTouchEnd = handleEnd;

    containerEl.addEventListener('mousedown', handleMouseDown);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('mouseleave', handleMouseUp);

    containerEl.addEventListener('touchstart', handleTouchStart);
    containerEl.addEventListener('touchmove', handleTouchMove);
    window.addEventListener('touchend', handleTouchEnd);

    return () => {
      containerEl.removeEventListener('mousedown', handleMouseDown);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);

      containerEl.removeEventListener('touchstart', handleTouchStart);
      containerEl.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, [counter]);

  return (
    <>
      <div className={styles.fav} data-is-patchwork>
        <FavouritesButton genericVehicleListings={vehicle} pageType={PAGE_TYPE.Pdp} />
      </div>
      <div
        className={styles.container}
        data-was-used={hasBeenUsed}
        data-is-loading={isLoading}
        data-is-initialised={isInitialised}
        data-test-id="image-360-container"
      >
        <link rel="preload" as="image" href={images[0]} />
        <div className={styles.fav} data-is-patchwork={false}>
          <FavouritesButton genericVehicleListings={vehicle} pageType="pdp" />
        </div>
        <div className={styles.initialising}>
          <Loading locale={'none' as Locale} />
        </div>
        <div className={styles.heading} data-alt-view={false}>
          {galleryStrings?.view360}
        </div>
        <div className={styles.heading} data-alt-view>
          {galleryStrings?.dragToRotate}
        </div>
        <Arrow360 />

        <div className={styles.images} ref={containerRef}>
          {images.map((image, index) => (
            <img
              key={index}
              src={image}
              alt="360 view"
              data-complete={false}
              className={styles.image}
              draggable={false}
              onLoad={e => onImageLoad(e, index)}
              fetchPriority={index === currentIndexRef.current ? 'high' : 'low'}
            />
          ))}
        </div>
      </div>
    </>
  );
};
